import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

const panels = {
  0: "analyse",
  1: "annoate"
}

// intial state is the object used to set the  state slice default values, its field are:
//  - main_sidebar_panel_active // state used to set active tab of the main side bar (calues "analyse" or "annotate")
//  - annotation_method // state defining the type of draw for tha nnotation (values: "point", "polygon", "line", "square" => last one is disabled)
//  - selectedAnnotationIds // state containing the list of the ids of annotations selected, type: Array
//  - cursorPositionInMap // state used to store the cursor live position (tracking in pointerMoveAction in MapWrapper.jsx display in CursorPosition.jsx)
//  - currentResoltionInMap / /state used for the screen ruler in inspection.js
//  - coordSysX // state used to store the inspection coordSysX => see inspection.js for structure
//  - coordSysY // state used to store the inspection coordSysY => see inspection.js for structure
//  - topbar // state used to store the inspection name
//  - annotationsForGeometryEdit // state used to handle geometry updates; contains the id of the annotation being edited
//    Set by interactions:
//      * Annotate panel buttons: Redraw, Modify, Partial Redraw (single annotation only)
//      * Translate interaction: on translateend (after dropping the feature)
//    Constraints: length === 1 for single-annotation operations (Redraw, Modify, Partial Redraw)
//  - newGeometries // state used to handle geometry update, it contains the new geometries. null/undefined otherwise
//    Constraints: length === 1 for single-annotation operations (Redraw, Modify, Partial Redraw)
//  - validateModifyOnAnnotation // state used to handle geometry update, it contains the validation state to trigger the update. false/true otherwisemetries. null/undefined otherwise

const initialState = {
  main_sidebar_panel_active: "annotate",
  annotation_method: "point",
  // Select tool: which list a click reads. "annotation" or "text"
  annotationSelectTarget: "annotation",
  selectedAnnotationIds: [],
  annotationsForGeometryEdit: [],
  newGeometries: [],
  validateModifyOnAnnotation: false,
  cursorPositionInMap: [0,0],
  currentResoltionInMap: 1,
  coordSysX: undefined,
  coordSysY: undefined,
  topbar: {
    title: ""
  },
  selectionBoxExtent: undefined,
  moveToAnnotationId: null, // ID of annotation to move view to (triggers moveViewToAnnotation in MapWrapper)
  displayed_photo: null, // photo currently shown in viewer / popup
  displayed_photo_for_annotations: null, // photo used to filter annotations on the map (can lag behind for perf)
  // Translate modal: offset filled by two-click measure (inspection units)
  translateOffsetDraft: null, // { x, y } | null
  // Text label modal: point filled by the text click (reference coordinates)
  textLabelDraft: null, // [x, y] | null
}


export const interfaceSlice = createSlice({
  name: 'interface',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    togglePanel: (state,{payload: {id}}) => {
      // Redux Toolkit allows us to write "mutating" logic in reducers. It
      // doesn't actually mutate the state because it uses the Immer library,
      // which detects changes to a "draft state" and produces a brand new
      // immutable state based off those changes
      return {
        ...state,
        main_sidebar_panel_active: id
      }
    },
    setAnnotationSelectTarget: (state, {payload}) => {
      return {
        ...state,
        annotationSelectTarget: payload === "text" ? "text" : "annotation",
      }
    },
    toggleAnnotationMethod: (state,{payload: {id}}) => {
      // Redux Toolkit allows us to write "mutating" logic in reducers. It
      // doesn't actually mutate the state because it uses the Immer library,
      // which detects changes to a "draft state" and produces a brand new
      // immutable state based off those changes
      return {
        ...state,
        annotation_method: id
      }
    },
    toggleCrackmeter: (state,{payload: {active}}) => {
      return {
        ...state,
        crackmeter_active: active
      }
    },
    selectAnnotation: (state,{payload}) => {
      // selectAnnotation handle the list of selected annotations
      //  - it takes an id as a param
      //  - the id is added or removed depending on it's presence in selectedAnnotationIds (array of ids)
      let selected_ids = [...state.selectedAnnotationIds];
      if (state.selectedAnnotationIds.includes(payload)) {
        // remove id to unselect annotation
        selected_ids = selected_ids.filter(el => el !== payload);
      } else {
        // add id to seletion array
        selected_ids = selected_ids = [...selected_ids, payload];
      }
      return {
        ...state,
        selectedAnnotationIds: selected_ids
      }
    },
    selectMultipleAnnotations: (state, {payload}) => {
      // selectMultipleAnnotations handle a list of selected annotations
      //  - it takes an array as a param
      //  - Empty array => unselect all
      //  - Array content is defined as the new selection of annotations
      // OPTIMIZATION: Only update if selection actually changed
      const currentIds = state.selectedAnnotationIds;
      if (currentIds.length === payload.length && 
          currentIds.every((id, idx) => id === payload[idx])) {
        // No change, return state as-is
        return state;
      }
      return {
        ...state,
        selectedAnnotationIds: payload
      }
    },
    setAnnotationsForGeometryEdit: (state, {payload}) => {
      return {
        ...state,
        annotationsForGeometryEdit: payload
      }
    },
    setNewGeometries :(state, {payload}) => {
      return {
        ...state,
        newGeometries: payload
      }
    },
    setValidateModifyOnAnnotation: (state, {payload}) => {
      state.validateModifyOnAnnotation = payload;
    },
    setTopbar: (state, {payload}) => {
      return {
        ...state,
        topbar: {
          ...state.topbar,
          ...payload
        }
      }
    },
    setDisplayedPhoto: (state, {payload}) => {
      return {
        ...state,
        displayed_photo: payload
      }
    },
    setDisplayedPhotoForAnnotations: (state, {payload}) => {
      return {
        ...state,
        displayed_photo_for_annotations: payload
      }
    },

    setMoveToAnnotationId: (state, {payload}) => {
      return {
        ...state,
        moveToAnnotationId: payload
      }
    },
    setTranslateOffsetDraft: (state, {payload}) => {
      return {
        ...state,
        translateOffsetDraft: payload
      }
    },
    setTextLabelDraft: (state, {payload}) => {
      return {
        ...state,
        textLabelDraft: payload
      }
    },
  }
});

export const { 
  togglePanel,
  toggleAnnotationMethod,
  setAnnotationSelectTarget,
  toggleCrackmeter,
  selectAnnotation,
  selectMultipleAnnotations,
  setAnnotationsForGeometryEdit,
  setNewGeometries,
  setValidateModifyOnAnnotation,
  setTopbar,
  setDisplayedPhoto,
  setDisplayedPhotoForAnnotations,
  setMoveToAnnotationId,
  setTranslateOffsetDraft,
  setTextLabelDraft,
} = interfaceSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectActivePanel = (state) => state.interface.main_sidebar_panel_active
export const selectAnnotationMethod = (state) => state.interface.annotation_method
export const selectAnnotationSelectTarget = (state) => state.interface.annotationSelectTarget
export const selectSelectedAnnotationIds = (state) => state.interface.selectedAnnotationIds
export const getAnnotationsForGeometryEdit = (state) => state.interface.annotationsForGeometryEdit
export const getNewGeometries = (state) => state.interface.newGeometries
export const getValidateModifyOnAnnotation = (state) => state.interface.validateModifyOnAnnotation
export const selectTopbarTitle = (state) => state.interface.topbar.title
export const selectDisplayedPhoto = (state) => state.interface.displayed_photo
export const selectDisplayedPhotoForAnnotations = (state) => state.interface.displayed_photo_for_annotations
/**
 * Selector to get the current annotation ID that the map should move to.
 * Returns null if no move is requested.
 */
export const selectMoveToAnnotationId = (state) => state.interface.moveToAnnotationId
export const selectTranslateOffsetDraft = (state) => state.interface.translateOffsetDraft
export const selectTextLabelDraft = (state) => state.interface.textLabelDraft
export default interfaceSlice.reducer;